import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { sendContactEmail } from '../services/contactEmail';
import { textFieldDarkSx, glassDialogPaperSx } from './dialogDarkGlass';

interface ContactDialogProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    subtitle?: string;
    initialMessage?: string;
    footerNote?: string;
}

const ContactDialog: React.FC<ContactDialogProps> = ({
    open,
    onClose,
    title = 'Contáctanos',
    subtitle = 'Completa el formulario y te responderemos a la brevedad.',
    initialMessage,
    footerNote,
}) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState(initialMessage ?? '');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await sendContactEmail({ name, email, message });

            setSuccess(true);
            analytics.track(AnalyticsEvent.SUBMIT_CONTACT_FORM, {
                source: title,
            });
            setName('');
            setEmail('');
            setMessage('');

            // Close after 2 seconds
            setTimeout(() => {
                setSuccess(false);
                onClose();
            }, 2000);
        } catch (err) {
            setError('Error al enviar el mensaje. Por favor intenta nuevamente.');
            console.error('EmailJS error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setName('');
            setEmail('');
            setMessage(initialMessage ?? '');
            setError('');
            setSuccess(false);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: glassDialogPaperSx,
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    backgroundColor: 'transparent',
                }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            fontFamily: '"DM Serif Display", serif',
                            color: 'white',
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.9rem',
                            color: 'rgba(255, 255, 255, 0.65)',
                            mt: 0.5,
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: 'rgba(255,255,255,0.65)', '&:hover': { color: 'white' } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ pt: 2, backgroundColor: 'transparent' }}>
                    {success && (
                        <Alert
                            severity="success"
                            sx={{
                                mb: 2,
                                bgcolor: 'rgba(76, 175, 80, 0.18)',
                                color: '#c8e6c9',
                                border: '1px solid rgba(129, 199, 132, 0.45)',
                                '& .MuiAlert-icon': { color: '#a5d6a7' },
                            }}
                        >
                            ¡Mensaje enviado con éxito! Te contactaremos pronto.
                        </Alert>
                    )}

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2,
                                bgcolor: 'rgba(244, 67, 54, 0.15)',
                                color: '#ffcdd2',
                                border: '1px solid rgba(239, 83, 80, 0.45)',
                                '& .MuiAlert-icon': { color: '#ef9a9a' },
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <TextField
                        autoFocus
                        required
                        fullWidth
                        label="Nombre"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        disabled={loading || success}
                        sx={{ mb: 2, ...textFieldDarkSx }}
                    />

                    <TextField
                        required
                        fullWidth
                        type="email"
                        label="Email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        disabled={loading || success}
                        sx={{ mb: 2, ...textFieldDarkSx }}
                    />

                    <TextField
                        required
                        fullWidth
                        multiline
                        rows={4}
                        label="Mensaje"
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        disabled={loading || success}
                        placeholder="¿En qué podemos ayudarte?"
                        sx={textFieldDarkSx}
                    />
                </DialogContent>

                <DialogActions
                    sx={{ px: 3, pb: footerNote ? 1 : 3, pt: 1, backgroundColor: 'transparent' }}
                >
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            color: 'rgba(255, 255, 255, 0.65)',
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || success || !name || !email || !message}
                        endIcon={
                            loading ? (
                                <CircularProgress size={18} sx={{ color: 'white' }} />
                            ) : (
                                <SendIcon />
                            )
                        }
                        sx={{
                            backgroundColor: 'rgba(255,255,255,0.14)',
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.22)',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            borderRadius: '10px',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.22)',
                                boxShadow: 'none',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(255,255,255,0.06)',
                                color: 'rgba(255,255,255,0.35)',
                                borderColor: 'rgba(255,255,255,0.1)',
                            },
                        }}
                    >
                        {loading ? 'Enviando...' : 'Enviar'}
                    </Button>
                </DialogActions>

                {footerNote && (
                    <Typography
                        sx={{
                            px: 3,
                            pb: 3,
                            pt: 1,
                            fontSize: '0.78rem',
                            color: 'rgba(255, 255, 255, 0.45)',
                            fontFamily: '"DM Sans", sans-serif',
                            lineHeight: 1.5,
                            backgroundColor: 'transparent',
                        }}
                    >
                        {footerNote}
                    </Typography>
                )}
            </form>
        </Dialog>
    );
};

export default ContactDialog;
